import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { CoinValue } from './types';

export const NORMALIZED_COIN_RADIUS = 0.5;
export const NORMALIZED_COIN_THICKNESS = 0.1;

const FACE_RADIUS = 0.46;
const FACE_HALF_HEIGHT = NORMALIZED_COIN_THICKNESS * 0.5;
const RIM_HALF_HEIGHT = 0.033;
const RELIEF_DEPTH = 0.009;

type Vec2 = readonly [number, number];
type Vec3 = readonly [number, number, number];

const SEVEN_SEGMENTS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  '0': Object.freeze(['a', 'b', 'c', 'd', 'e', 'f']),
  '1': Object.freeze(['b', 'c']),
  '2': Object.freeze(['a', 'b', 'g', 'e', 'd']),
  '3': Object.freeze(['a', 'b', 'g', 'c', 'd']),
  '4': Object.freeze(['f', 'g', 'b', 'c']),
  '5': Object.freeze(['a', 'f', 'g', 'c', 'd']),
  '6': Object.freeze(['a', 'f', 'g', 'e', 'c', 'd']),
  '7': Object.freeze(['a', 'b', 'c']),
  '8': Object.freeze(['a', 'b', 'c', 'd', 'e', 'f', 'g']),
  '9': Object.freeze(['a', 'b', 'c', 'd', 'f', 'g']),
});

/**
 * A single normalized, reeded coin body shared by every denomination.
 * Material group 0 is the face/bevel; material group 1 is the reeded edge.
 */
export function createSharedCoinBodyGeometry(radialSegments = 48): THREE.BufferGeometry {
  const segments = Math.max(24, Math.floor(radialSegments / 2) * 2);
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];

  const appendVertex = (position: Vec3, normal: Vec3, uv: Vec2): void => {
    positions.push(...position);
    normals.push(...normal);
    uvs.push(...uv);
  };

  const appendTriangle = (
    a: Vec3,
    b: Vec3,
    c: Vec3,
    normalA: Vec3,
    normalB: Vec3,
    normalC: Vec3,
    uvA: Vec2,
    uvB: Vec2,
    uvC: Vec2,
  ): void => {
    appendVertex(a, normalA, uvA);
    appendVertex(b, normalB, uvB);
    appendVertex(c, normalC, uvC);
  };

  const faceStart = positions.length / 3;
  const topNormal: Vec3 = [0, 1, 0];
  const bottomNormal: Vec3 = [0, -1, 0];

  for (let index = 0; index < segments; index += 1) {
    const angle0 = (index / segments) * Math.PI * 2;
    const angle1 = ((index + 1) / segments) * Math.PI * 2;
    const topCenter: Vec3 = [0, FACE_HALF_HEIGHT, 0];
    const top0: Vec3 = [Math.cos(angle0) * FACE_RADIUS, FACE_HALF_HEIGHT, Math.sin(angle0) * FACE_RADIUS];
    const top1: Vec3 = [Math.cos(angle1) * FACE_RADIUS, FACE_HALF_HEIGHT, Math.sin(angle1) * FACE_RADIUS];
    const bottomCenter: Vec3 = [0, -FACE_HALF_HEIGHT, 0];
    const bottom0: Vec3 = [top0[0], -FACE_HALF_HEIGHT, top0[2]];
    const bottom1: Vec3 = [top1[0], -FACE_HALF_HEIGHT, top1[2]];
    const uv0: Vec2 = [0.5 + Math.cos(angle0) * 0.46, 0.5 + Math.sin(angle0) * 0.46];
    const uv1: Vec2 = [0.5 + Math.cos(angle1) * 0.46, 0.5 + Math.sin(angle1) * 0.46];

    appendTriangle(topCenter, top1, top0, topNormal, topNormal, topNormal, [0.5, 0.5], uv1, uv0);
    appendTriangle(bottomCenter, bottom0, bottom1, bottomNormal, bottomNormal, bottomNormal, [0.5, 0.5], uv0, uv1);
  }

  const radialNormal = (angle: number, y: number): Vec3 => {
    const outward = 0.37;
    return [Math.cos(angle) * outward, y * 0.929, Math.sin(angle) * outward];
  };

  for (let index = 0; index < segments; index += 1) {
    const angle0 = (index / segments) * Math.PI * 2;
    const angle1 = ((index + 1) / segments) * Math.PI * 2;
    const outerRadius0 = reededRadius(index);
    const outerRadius1 = reededRadius(index + 1);
    const innerTop0: Vec3 = [Math.cos(angle0) * FACE_RADIUS, FACE_HALF_HEIGHT, Math.sin(angle0) * FACE_RADIUS];
    const innerTop1: Vec3 = [Math.cos(angle1) * FACE_RADIUS, FACE_HALF_HEIGHT, Math.sin(angle1) * FACE_RADIUS];
    const outerTop0: Vec3 = [Math.cos(angle0) * outerRadius0, RIM_HALF_HEIGHT, Math.sin(angle0) * outerRadius0];
    const outerTop1: Vec3 = [Math.cos(angle1) * outerRadius1, RIM_HALF_HEIGHT, Math.sin(angle1) * outerRadius1];
    const innerBottom0: Vec3 = [innerTop0[0], -FACE_HALF_HEIGHT, innerTop0[2]];
    const innerBottom1: Vec3 = [innerTop1[0], -FACE_HALF_HEIGHT, innerTop1[2]];
    const outerBottom0: Vec3 = [outerTop0[0], -RIM_HALF_HEIGHT, outerTop0[2]];
    const outerBottom1: Vec3 = [outerTop1[0], -RIM_HALF_HEIGHT, outerTop1[2]];
    const topN0 = radialNormal(angle0, 1);
    const topN1 = radialNormal(angle1, 1);
    const bottomN0 = radialNormal(angle0, -1);
    const bottomN1 = radialNormal(angle1, -1);
    const u0 = index / segments;
    const u1 = (index + 1) / segments;

    appendTriangle(innerTop0, outerTop1, outerTop0, topN0, topN1, topN0, [u0, 1], [u1, 0], [u0, 0]);
    appendTriangle(innerTop0, innerTop1, outerTop1, topN0, topN1, topN1, [u0, 1], [u1, 1], [u1, 0]);
    appendTriangle(
      innerBottom0,
      outerBottom0,
      outerBottom1,
      bottomN0,
      bottomN0,
      bottomN1,
      [u0, 0],
      [u0, 1],
      [u1, 1],
    );
    appendTriangle(
      innerBottom0,
      outerBottom1,
      innerBottom1,
      bottomN0,
      bottomN1,
      bottomN1,
      [u0, 0],
      [u1, 1],
      [u1, 0],
    );
  }

  const faceCount = positions.length / 3 - faceStart;
  const rimStart = positions.length / 3;

  for (let index = 0; index < segments; index += 1) {
    const angle0 = (index / segments) * Math.PI * 2;
    const angle1 = ((index + 1) / segments) * Math.PI * 2;
    const radius0 = reededRadius(index);
    const radius1 = reededRadius(index + 1);
    const top0: Vec3 = [Math.cos(angle0) * radius0, RIM_HALF_HEIGHT, Math.sin(angle0) * radius0];
    const top1: Vec3 = [Math.cos(angle1) * radius1, RIM_HALF_HEIGHT, Math.sin(angle1) * radius1];
    const bottom0: Vec3 = [top0[0], -RIM_HALF_HEIGHT, top0[2]];
    const bottom1: Vec3 = [top1[0], -RIM_HALF_HEIGHT, top1[2]];
    const dx = top1[0] - top0[0];
    const dz = top1[2] - top0[2];
    const inverseLength = 1 / Math.max(0.000_001, Math.hypot(dx, dz));
    const facetNormal: Vec3 = [dz * inverseLength, 0, -dx * inverseLength];
    const u0 = index / segments;
    const u1 = (index + 1) / segments;

    appendTriangle(top0, bottom1, bottom0, facetNormal, facetNormal, facetNormal, [u0, 1], [u1, 0], [u0, 0]);
    appendTriangle(top0, top1, bottom1, facetNormal, facetNormal, facetNormal, [u0, 1], [u1, 1], [u1, 0]);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.addGroup(faceStart, faceCount, 0);
  geometry.addGroup(rimStart, positions.length / 3 - rimStart, 1);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.name = 'SharedBeveledReededCoinBody';
  return geometry;
}

/** Creates a shared two-sided relief: denomination on +Y, mint crest on -Y. */
export function createCoinReliefGeometry(value: CoinValue): THREE.BufferGeometry {
  const geometries: THREE.BufferGeometry[] = [];

  const obverseRing = new THREE.TorusGeometry(0.405, 0.011, 4, 24);
  obverseRing.rotateX(-Math.PI / 2);
  obverseRing.translate(0, FACE_HALF_HEIGHT + 0.004, 0);
  geometries.push(toMergeReady(obverseRing));

  const reverseRing = new THREE.TorusGeometry(0.405, 0.011, 4, 24);
  reverseRing.rotateX(-Math.PI / 2);
  reverseRing.translate(0, -FACE_HALF_HEIGHT - 0.004, 0);
  geometries.push(toMergeReady(reverseRing));

  const denominationShapes = createDenominationShapes(String(value));
  const denomination = createReliefExtrusion(denominationShapes);
  denomination.rotateX(-Math.PI / 2);
  denomination.translate(0, FACE_HALF_HEIGHT + 0.001, 0);
  geometries.push(toMergeReady(denomination));

  const crest = createReliefExtrusion(createReverseCrestShapes(value));
  crest.rotateX(Math.PI / 2);
  crest.translate(0, -FACE_HALF_HEIGHT - 0.001, 0);
  geometries.push(toMergeReady(crest));

  const merged = mergeGeometries(geometries, false);
  for (const geometry of geometries) geometry.dispose();
  if (!merged) throw new Error(`Could not build relief geometry for coin value ${value}.`);
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  merged.name = `CoinRelief${value}`;
  return merged;
}

function reededRadius(index: number): number {
  return NORMALIZED_COIN_RADIUS - (index % 2 === 0 ? 0 : 0.007);
}

function createReliefExtrusion(shapes: THREE.Shape[]): THREE.ExtrudeGeometry {
  return new THREE.ExtrudeGeometry(shapes, {
    depth: RELIEF_DEPTH,
    steps: 1,
    curveSegments: 1,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.003,
    bevelThickness: 0.002,
  });
}

function createDenominationShapes(label: string): THREE.Shape[] {
  const digitWidth = label.length === 1 ? 0.125 : label.length === 2 ? 0.096 : 0.071;
  const digitHeight = digitWidth * 1.78;
  const thickness = digitWidth * 0.2;
  const gap = digitWidth * 0.2;
  const totalWidth = label.length * digitWidth + (label.length - 1) * gap;
  const shapes: THREE.Shape[] = [];

  for (let digitIndex = 0; digitIndex < label.length; digitIndex += 1) {
    const digit = label[digitIndex];
    const activeSegments = SEVEN_SEGMENTS[digit];
    if (!activeSegments) continue;
    const centerX = -totalWidth * 0.5 + digitWidth * 0.5 + digitIndex * (digitWidth + gap);
    for (const segment of activeSegments) {
      const descriptor = getSegmentDescriptor(segment, digitWidth, digitHeight, thickness);
      shapes.push(
        createChamferedBarShape(
          centerX + descriptor.x,
          descriptor.y,
          descriptor.width,
          descriptor.height,
        ),
      );
    }
  }
  return shapes;
}

function getSegmentDescriptor(
  segment: string,
  digitWidth: number,
  digitHeight: number,
  thickness: number,
): { x: number; y: number; width: number; height: number } {
  const horizontalWidth = digitWidth - thickness * 0.55;
  const verticalHeight = digitHeight * 0.5 - thickness * 0.62;
  const x = digitWidth * 0.5 - thickness * 0.52;
  const y = digitHeight * 0.25;
  switch (segment) {
    case 'a':
      return { x: 0, y: digitHeight * 0.5, width: horizontalWidth, height: thickness };
    case 'b':
      return { x, y, width: thickness, height: verticalHeight };
    case 'c':
      return { x, y: -y, width: thickness, height: verticalHeight };
    case 'd':
      return { x: 0, y: -digitHeight * 0.5, width: horizontalWidth, height: thickness };
    case 'e':
      return { x: -x, y: -y, width: thickness, height: verticalHeight };
    case 'f':
      return { x: -x, y, width: thickness, height: verticalHeight };
    default:
      return { x: 0, y: 0, width: horizontalWidth, height: thickness };
  }
}

function createChamferedBarShape(centerX: number, centerY: number, width: number, height: number): THREE.Shape {
  const shape = new THREE.Shape();
  const halfWidth = width * 0.5;
  const halfHeight = height * 0.5;
  const chamfer = Math.min(halfWidth, halfHeight) * 0.45;
  shape.moveTo(centerX - halfWidth + chamfer, centerY - halfHeight);
  shape.lineTo(centerX + halfWidth - chamfer, centerY - halfHeight);
  shape.lineTo(centerX + halfWidth, centerY - halfHeight + chamfer);
  shape.lineTo(centerX + halfWidth, centerY + halfHeight - chamfer);
  shape.lineTo(centerX + halfWidth - chamfer, centerY + halfHeight);
  shape.lineTo(centerX - halfWidth + chamfer, centerY + halfHeight);
  shape.lineTo(centerX - halfWidth, centerY + halfHeight - chamfer);
  shape.lineTo(centerX - halfWidth, centerY - halfHeight + chamfer);
  shape.closePath();
  return shape;
}

function createReverseCrestShapes(value: CoinValue): THREE.Shape[] {
  const rank = value === 1 ? 1 : value === 5 ? 2 : value === 10 ? 3 : value === 25 ? 4 : value === 50 ? 5 : 6;
  const shapes = [createStarShape(0, 0, 0.1, 0.045, 6)];
  for (let index = 0; index < rank; index += 1) {
    const angle = -Math.PI * 0.5 + (index / rank) * Math.PI * 2;
    shapes.push(createRegularPolygonShape(Math.cos(angle) * 0.2, Math.sin(angle) * 0.2, 0.025, 6));
  }
  return shapes;
}

function createStarShape(
  centerX: number,
  centerY: number,
  outerRadius: number,
  innerRadius: number,
  points: number,
): THREE.Shape {
  const shape = new THREE.Shape();
  for (let index = 0; index < points * 2; index += 1) {
    const angle = -Math.PI * 0.5 + (index / (points * 2)) * Math.PI * 2;
    const radius = index % 2 === 0 ? outerRadius : innerRadius;
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;
    if (index === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  return shape;
}

function createRegularPolygonShape(
  centerX: number,
  centerY: number,
  radius: number,
  sides: number,
): THREE.Shape {
  const shape = new THREE.Shape();
  for (let index = 0; index < sides; index += 1) {
    const angle = -Math.PI * 0.5 + (index / sides) * Math.PI * 2;
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;
    if (index === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  return shape;
}

function toMergeReady(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!geometry.index) return geometry;
  const result = geometry.toNonIndexed();
  geometry.dispose();
  return result;
}
